import type { TicketMessage } from '@/types/ticket'
import { SystemMessageRow } from './SystemMessageRow'
import { AttachmentList } from '../attachments/AttachmentList'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import dayjs from 'dayjs'

function authorName(message: TicketMessage): string {
  return message.author?.name ?? 'Unknown'
}

function isStaff(message: TicketMessage): boolean {
  return Boolean(message.author?.isAdmin) || message.authorRole === 'admin'
}

export function MessageThread({
  ticketId,
  messages,
}: {
  ticketId: string
  messages: TicketMessage[]
}) {
  return (
    <div className="space-y-4 p-4">
      {messages.map(m => {
        if (m.kind === 'system') return <SystemMessageRow key={m._id} message={m} />

        const staff = isStaff(m)
        // Optimistic messages (still in flight) render dimmed until the server
        // confirms and the refetch swaps in the real document.
        const pending = m._id.startsWith('optimistic-')
        // Visual distinction: internal notes are amber, staff replies get a
        // brand-green left rail + tinted card and indent in from the left,
        // customer replies stay neutral and flush-left. The asymmetry makes it
        // obvious at a glance who said what when scanning the thread.
        const cardClass = m.isInternal
          ? 'border-l-2 border-l-amber-400 bg-amber-50 dark:bg-amber-900/20'
          : staff
          ? 'border-l-2 border-l-brand-500 bg-brand-50/60 ml-6'
          : 'bg-background mr-6'

        return (
          <div key={m._id} className={`rounded border p-4 ${cardClass} ${pending ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <span className="text-foreground font-medium">{authorName(m)}</span>
              {staff ? (
                <span className="rounded-sm bg-brand-500 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-white">
                  Staff
                </span>
              ) : (
                <span className="rounded-sm bg-muted px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Customer
                </span>
              )}
              <span aria-hidden>·</span>
              <span>{dayjs(m.createdAt).format('MMM D, HH:mm')}</span>
              {m.isInternal && (
                <>
                  <span aria-hidden>·</span>
                  <span className="font-medium text-amber-700">Internal note</span>
                </>
              )}
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.body}</ReactMarkdown>
            </div>
            <AttachmentList ticketId={ticketId} attachments={m.attachments} />
          </div>
        )
      })}
    </div>
  )
}
